import {Check, Loader2, Mail} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { emailErrorMessage } from '@/lib/authErrors'
import { sendStepByStepSolution } from '@/lib/emailSolution'

interface Props {
  title: string
  markdown: string
  className?: string
}

function EmailSolutionButton({ title, markdown, className = '' }: Props) {
  const { isAuthenticated, user } = useAuth()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [error, setError] = useState('')

  const handle = async () => {
    if (!isAuthenticated) {
      navigate('/auth')
      return
    }
    setStatus('sending')
    setError('')
    try {
      await sendStepByStepSolution(title, markdown)
      setStatus('sent')
      setTimeout(() => setStatus('idle'), 2200)
    }
    catch (err) {
      setStatus('error')
      setError(emailErrorMessage(err))
    }
  }

  return (
    <div className={className}>
      <button
        onClick={handle}
        disabled={status === 'sending'}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium border border-[var(--line-strong)] bg-white/[0.03] text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300 transition disabled:opacity-60"
        type="button"
      >
        {status === 'sending' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        {status === 'sent' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : null}
        {status !== 'sending' && status !== 'sent' ? <Mail className="w-3.5 h-3.5" /> : null}
        {status === 'sending' ? 'Sending...' : status === 'sent' ? 'Emailed' : isAuthenticated ? 'Email solution' : 'Sign in to email'}
      </button>
      {isAuthenticated && user?.email ? (
        <div className="mt-1.5 text-[11px] text-slate-500">Sends to {user.email}</div>
      ) : null}
      {status === 'error' && error ? (
        <div className="mt-1.5 text-[11px] text-rose-300">{error}</div>
      ) : null}
    </div>
  )
}

export default EmailSolutionButton
